import React from 'react';

// ---------------------------------------------------------------------------
// Denial Resolve v4 result view.
//
// Renders the v4 Denial Resolution Case File for an RCM specialist as five
// sections (A-E). Unlike DenialResolveV2View (which projects v2/v3 node
// outputs into UI), the v4 view consumes a single persisted CaseFile JSON
// document fetched via GET /dm-api/denials/v4/cases/<case_id>.
//
// RCM-facing sections (each lets the specialist answer one question fast):
//   A. Case Resolution Summary         -- what's at stake + next step
//   B. Denial Findings & Mitigation    -- per-line denial + supporting evidence
//   C. Clinical & Claim Context        -- case background parsed into rows
//   D. Evidence Discovery Approach     -- evidence checklist + RCM use
//   E. Appeal / Submission Packet      -- letter, attachments, crosswalk, steps
//
// The original 13 internal sections (root cause, lookup trace, source graph
// trace, deterministic findings, SNN reasoning, audit trail, learning tags,
// learning signal, payer contradictions raw, packet outputs raw, human
// review flags) are still exported as named exports so DenialCaseRunPage's
// DeveloperDrawer can keep showing them under a collapsed "Engine internals"
// panel for debugging and audit.
// ---------------------------------------------------------------------------

const DASH = '-';

// ---------------------------------------------------------------------------
// Defensive text renderer.
//
// React throws "Objects are not valid as a React child" (#31) the moment a
// raw object slips into JSX. The v4 pipeline writes several fields whose
// schema has drifted between runs (e.g. `key_arguments` is sometimes a
// list of strings, sometimes a list of {denial_reason, contradicting_evidence,
// verbatim_source_clause} objects from the s7a appeal-letter generator).
// `toText()` is the one place that normalizes any value into a renderable
// string so a single shape change can never crash the whole Case File view.
// ---------------------------------------------------------------------------
function toText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    return v.map(toText).filter(Boolean).join('; ');
  }
  if (typeof v === 'object') {
    // Common shapes from the v4 LLM outputs. Prefer a human-readable
    // single sentence over a JSON dump when we recognise the shape.
    if (v.denial_reason || v.contradicting_evidence || v.verbatim_source_clause) {
      const parts = [];
      if (v.denial_reason) parts.push(`Payer: ${toText(v.denial_reason)}`);
      if (v.contradicting_evidence) parts.push(`CM evidence: ${toText(v.contradicting_evidence)}`);
      if (v.verbatim_source_clause) parts.push(`Source: "${toText(v.verbatim_source_clause)}"`);
      return parts.join(' | ');
    }
    if (v.argument || v.point) return toText(v.argument || v.point);
    if (v.name && v.purpose) return `${v.name} -- ${v.purpose}`;
    if (v.name) return toText(v.name);
    if (v.text) return toText(v.text);
    if (v.label) return toText(v.label);
    try {
      return JSON.stringify(v);
    } catch (_e) {
      return String(v);
    }
  }
  return String(v);
}

function fmtCurrency(v) {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  return n.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtPct(v) {
  if (v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  if (Number.isNaN(n)) return DASH;
  if (n <= 1) return `${Math.round(n * 100)}%`;
  return `${Math.round(n)}%`;
}

function humanAction(action) {
  if (!action) return DASH;
  return {
    file_appeal: 'File appeal',
    appeal: 'File appeal',
    submit_corrected_claim: 'Submit corrected claim',
    corrected_claim: 'Submit corrected claim',
    coder_review: 'Send for coder review',
    documentation_submission: 'Submit documentation',
    payer_follow_up: 'Follow up with payer',
    cob_resubmission: 'Resubmit with COB proof',
    auth_correction: 'Correct authorization',
    coding_review: 'Send for coding review',
    clinical_review: 'Send for clinical review',
    underpayment_dispute: 'Dispute underpayment',
    write_off: 'Write off',
    patient_responsibility: 'Bill patient responsibility',
    human_review: 'Send to human review',
  }[action] || action.replace(/_/g, ' ');
}

function confTone(pct) {
  if (pct == null) return null;
  const n = Number(pct);
  const v = n <= 1 ? n * 100 : n;
  if (v >= 80) return 'good';
  if (v >= 50) return 'warn';
  return 'bad';
}

function severityTone(sev) {
  if (sev === 'critical') return 'bad';
  if (sev === 'warn') return 'warn';
  return 'good';
}

// ---------------------------------------------------------------------------
// Layout primitives (shared CSS classes with the v2 view)
// ---------------------------------------------------------------------------

function Empty({ children }) {
  return (
    <div className="denials-subsection__empty">
      {children || 'No data for this section.'}
    </div>
  );
}

function Chip({ label, value, tone, title }) {
  if (value === null || value === undefined || value === '' || value === DASH) return null;
  // Defensive: coerce non-scalar values so a schema drift never crashes the chip.
  const rendered = (typeof value === 'string' || typeof value === 'number'
    || typeof value === 'boolean' || React.isValidElement(value))
    ? value
    : toText(value);
  if (rendered === '' || rendered === null || rendered === undefined) return null;
  return (
    <div className={`denials-chip ${tone ? `denials-chip--${tone}` : ''}`} title={title || ''}>
      <span className="denials-chip__label">{label}</span>
      <span className="denials-chip__value">{rendered}</span>
    </div>
  );
}

function ChipRow({ children }) {
  return <div className="denials-chip-row">{children}</div>;
}

function KVRow({ k, v, tone }) {
  if (v === null || v === undefined || v === '') return null;
  const rendered = (typeof v === 'string' || typeof v === 'number'
    || typeof v === 'boolean' || React.isValidElement(v))
    ? v
    : toText(v);
  if (rendered === '' || rendered === null || rendered === undefined) return null;
  return (
    <div className="denials-kv">
      <span className="denials-kv__k">{k}</span>
      <span className={`denials-kv__v ${tone ? `denials-kv__v--${tone}` : ''}`}>{rendered}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 13 section renderers
// ---------------------------------------------------------------------------

function S2_RootCause({ rc }) {
  if (!rc || typeof rc !== 'object') return <Empty />;
  return (
    <div>
      <KVRow k="Primary cause" v={rc.primary_cause} />
      <KVRow k="Payer rationale" v={rc.payer_rationale} />
      <KVRow k="CM interpretation" v={rc.cm_interpretation} />
      <KVRow k="Classification" v={rc.classification_category} />
      {Array.isArray(rc.secondary_causes) && rc.secondary_causes.length > 0 && (
        <div className="denials-list">
          <div className="denials-list__title">Secondary causes</div>
          <ul>{rc.secondary_causes.map((s, i) => <li key={i}>{toText(s)}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function S3_LookupTrace({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;
  const okCount  = rows.filter((r) => r && r.status === 'ok').length;
  const errCount = rows.filter((r) => r && (r.status === 'error' || r.status === 'not_implemented')).length;
  const naCount  = rows.length - okCount - errCount;
  return (
    <div>
      <ChipRow>
        <Chip label="Lookups fired" value={rows.length} />
        <Chip label="OK"            value={okCount}  tone="good" />
        <Chip label="Not found"     value={naCount}  tone="warn" />
        <Chip label="Errored"       value={errCount} tone="bad" />
      </ChipRow>
      <table className="denials-table">
        <thead>
          <tr>
            <th>Lookup</th><th>Category</th><th>Status</th><th>Source</th><th>Used for</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><code>{toText(r.lookup_name)}</code></td>
              <td>{toText(r.category)}</td>
              <td>
                <span className={`denials-pill denials-pill--${r.status === 'ok' ? 'good'
                  : r.status === 'not_found' ? 'warn' : 'bad'}`}>
                  {toText(r.status) || DASH}
                </span>
              </td>
              <td>{toText(r.source) || DASH}{r.version ? ` (${toText(r.version)})` : ''}</td>
              <td>{Array.isArray(r.used_for) ? r.used_for.map(toText).filter(Boolean).join(', ') : DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function S4_SourceGraphTrace({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;

  // Group by purpose so a single retrieval of 1000+ clauses is browsable.
  const byPurpose = new Map();
  rows.forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const key = r.purpose || r.graph_name || r.graph_ref || 'other';
    if (!byPurpose.has(key)) byPurpose.set(key, []);
    byPurpose.get(key).push(r);
  });
  const groups = Array.from(byPurpose.entries()).sort((a, b) => b[1].length - a[1].length);

  return (
    <div>
      <ChipRow>
        <Chip label="Total clauses retrieved" value={rows.length} tone="good" />
        <Chip label="Distinct purposes" value={byPurpose.size} />
      </ChipRow>
      {groups.map(([purpose, items]) => (
        <details key={purpose} className="denials-disclosure" open={groups.length <= 3}>
          <summary>
            <strong>{purpose}</strong> &middot; {items.length} clause{items.length === 1 ? '' : 's'}
          </summary>
          <div className="denials-disclosure__body">
            {items.slice(0, 25).map((r, i) => (
              <div key={i} className="denials-quote">
                <div className="denials-quote__source">
                  <strong>{toText(r.graph_name || r.graph_ref) || '(graph)'}</strong>
                  {r.citation && <span> &middot; {toText(r.citation)}</span>}
                  {r.relevance_score != null && (
                    <span> &middot; relevance {fmtPct(r.relevance_score)}</span>
                  )}
                </div>
                {r.retrieved_clause && <blockquote>{toText(r.retrieved_clause)}</blockquote>}
              </div>
            ))}
            {items.length > 25 && (
              <div className="denials-disclosure__more">
                + {items.length - 25} more clauses in this purpose (truncated for display).
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function S6_DeterministicFindings({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;
  return (
    <table className="denials-table">
      <thead>
        <tr>
          <th>Rule ID</th><th>Rule</th><th>Result</th><th>Detail</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((f, i) => (
          <tr key={i}>
            <td><code>{toText(f.rule_id)}</code></td>
            <td>{toText(f.rule_text)}</td>
            <td>
              <span className={`denials-pill denials-pill--${f.result === 'pass' ? 'good'
                : f.result === 'fail' ? 'bad' : 'warn'}`}>
                {toText(f.result)}
              </span>
            </td>
            <td>{toText(f.detail) || DASH}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function S7_SNNFindings({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;
  return (
    <div>
      {rows.map((s, i) => (
        <div key={i} className="denials-finding">
          <ChipRow>
            <Chip label="Stage" value={s.stage} />
            <Chip label="Confidence" value={fmtPct(s.confidence)} tone={confTone(s.confidence)} />
          </ChipRow>
          <p>{toText(s.statement)}</p>
          {Array.isArray(s.grounded_in_deterministic) && s.grounded_in_deterministic.length > 0 && (
            <small>Grounded in rules: {s.grounded_in_deterministic.map(toText).filter(Boolean).join(', ')}</small>
          )}
        </div>
      ))}
    </div>
  );
}

function S11_PacketOutputs({ pkt }) {
  if (!pkt || typeof pkt !== 'object') return <Empty />;
  return (
    <div>
      {pkt.appeal_letter_required && (
        <div className="denials-packet">
          <h4>Appeal letter</h4>
          {pkt.appeal_letter_text ? (
            <pre className="denials-letter">{pkt.appeal_letter_text}</pre>
          ) : (
            <Empty>Letter text not yet generated.</Empty>
          )}
        </div>
      )}
      {pkt.corrected_claim_required && (
        <div className="denials-packet">
          <h4>Corrected claim</h4>
          {pkt.corrected_claim_instructions
            ? <p>{pkt.corrected_claim_instructions}</p>
            : <Empty>Instructions not yet generated.</Empty>}
        </div>
      )}
      {pkt.documentation_submission_required && (
        <div className="denials-packet">
          <h4>Documentation request</h4>
          {pkt.documentation_submission_instructions
            ? <p>{pkt.documentation_submission_instructions}</p>
            : <Empty>Instructions not yet generated.</Empty>}
        </div>
      )}
      {Array.isArray(pkt.attachments) && pkt.attachments.length > 0 && (
        <div className="denials-list">
          <div className="denials-list__title">Attachments</div>
          <ul>{pkt.attachments.map((a, i) => (
            <li key={i}>{typeof a === 'object' && a !== null
              ? `${toText(a.name)}${a.purpose ? ` -- ${toText(a.purpose)}` : ''}`
              : toText(a)}</li>
          ))}</ul>
        </div>
      )}
      {pkt.audit_summary && <KVRow k="Audit summary" v={pkt.audit_summary} />}
    </div>
  );
}

function S12_HumanReviewFlags({ flags }) {
  if (!Array.isArray(flags) || flags.length === 0) return <Empty>No human-review flags triggered.</Empty>;
  return (
    <div>
      {flags.map((f, i) => {
        const reasonText = toText(f.reason) || 'flag';
        return (
          <div key={i} className={`denials-alert denials-alert--${severityTone(f.severity)}`}>
            <strong>{reasonText.replace(/_/g, ' ')}</strong>
            {f.blocks_automation && <span> &middot; BLOCKS AUTOMATION</span>}
            <div>{toText(f.detail)}</div>
          </div>
        );
      })}
    </div>
  );
}

function S13_LearningTags({ tags }) {
  if (!tags || typeof tags !== 'object') return <Empty />;
  return (
    <div>
      <KVRow k="Payer" v={tags.payer} />
      <KVRow k="Denial type" v={tags.denial_type} />
      <KVRow k="Outcome path" v={tags.outcome_path} />
      {Array.isArray(tags.policy_clauses) && tags.policy_clauses.length > 0 && (
        <div className="denials-list">
          <div className="denials-list__title">Cited policy clauses</div>
          <ul>{tags.policy_clauses.map((c, i) => <li key={i}><code>{toText(c)}</code></li>)}</ul>
        </div>
      )}
      {Array.isArray(tags.evidence_types) && tags.evidence_types.length > 0 && (
        <div className="denials-list">
          <div className="denials-list__title">Evidence types</div>
          <ul>{tags.evidence_types.map((c, i) => <li key={i}>{toText(c)}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header chips (Run-mode / threshold / priority / multi-archetype)
// ---------------------------------------------------------------------------

function priorityTone(p) {
  if (p === 'high') return 'bad';
  if (p === 'medium') return 'warn';
  if (p === 'low') return 'good';
  return null;
}

// ---------------------------------------------------------------------------
// Per-line Denial Issues table (v3 section A3) -- kept for reuse by other
// engine views; the v4 RCM layout no longer renders this directly.
// ---------------------------------------------------------------------------

function actionTone(action) {
  if (action === 'submit_corrected_claim') return 'warn';
  if (action === 'file_appeal') return 'good';
  if (action === 'documentation_submission') return 'warn';
  if (action === 'split_appeal_and_corrected_claim') return 'warn';
  if (action === 'human_review' || action === 'human_review_required') return 'bad';
  return null;
}

// ---------------------------------------------------------------------------
// Extended Payer Contradictions (with line / section / local-filter / claim)
// ---------------------------------------------------------------------------

function S8ext_PayerContradictions({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;
  const hasExtra = rows.some((r) => r && (r.claim_line_no != null || r.section || r.local_filter || r.claim_code || r.source));
  return (
    <table className="denials-table denials-table--wide">
      <thead>
        <tr>
          {hasExtra && <th>#</th>}
          {hasExtra && <th>Source</th>}
          {hasExtra && <th>Claim</th>}
          {hasExtra && <th>Section</th>}
          <th>Payer assertion</th>
          <th>CM evidence</th>
          <th>Strength</th>
          <th>Appeal value</th>
          {hasExtra && <th>Local filter</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((c, i) => (
          <tr key={i}>
            {hasExtra && <td>{i + 1}</td>}
            {hasExtra && <td>{toText(c.source) || DASH}</td>}
            {hasExtra && <td>{toText(c.claim_code) || (c.claim_line_no != null ? `#${c.claim_line_no}` : DASH)}</td>}
            {hasExtra && <td>{toText(c.section) || DASH}</td>}
            <td className="denials-table__wrap">{toText(c.payer_assertion || c.denial_reason)}</td>
            <td className="denials-table__wrap">{toText(c.cm_evidence || c.contradicting_evidence)}</td>
            <td>
              <span className={`denials-pill denials-pill--${c.contradiction_strength === 'strong' ? 'good'
                : c.contradiction_strength === 'moderate' ? 'warn' : 'bad'}`}>
                {toText(c.contradiction_strength)}
              </span>
            </td>
            <td>{toText(c.appeal_usability) || (c.verbatim_source_clause
              ? <small className="denials-table__quote">"{toText(c.verbatim_source_clause)}"</small>
              : DASH)}</td>
            {hasExtra && <td><small>{toText(c.local_filter) || DASH}</small></td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Audit Trail (v3 section C6 = 7 named algorithmic steps)
// ---------------------------------------------------------------------------

function C6_AuditTrail({ steps }) {
  if (!Array.isArray(steps) || steps.length === 0) return <Empty />;
  return (
    <table className="denials-table">
      <thead>
        <tr><th>#</th><th>Step</th><th>Status</th><th>Detail</th></tr>
      </thead>
      <tbody>
        {steps.map((s, i) => (
          <tr key={i}>
            <td>{s.step_no || i + 1}</td>
            <td><strong>{toText(s.label)}</strong></td>
            <td>
              <span className={`denials-pill denials-pill--${s.status === 'completed' ? 'good'
                : s.status === 'failed' ? 'bad' : 'warn'}`}>
                {toText(s.status) || 'completed'}
              </span>
            </td>
            <td className="denials-table__wrap">{toText(s.detail) || DASH}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Learning Signal (extends S13 with chains, supported/unsupported issues)
// ---------------------------------------------------------------------------

function S13ext_LearningSignal({ ls }) {
  if (!ls || typeof ls !== 'object') return <Empty />;
  const ch = Array.isArray(ls.chains_fired) ? ls.chains_fired : [];
  const sup = Array.isArray(ls.supported_issues) ? ls.supported_issues : [];
  const unsup = Array.isArray(ls.unsupported_issues) ? ls.unsupported_issues : [];
  return (
    <div>
      <ChipRow>
        <Chip label="Outcome" value={ls.outcome} />
        <Chip label="Archetype" value={ls.archetype} />
        <Chip label="Payer" value={ls.payer_id} />
        <Chip label="Priority" value={ls.priority} tone={priorityTone(ls.priority)} />
        <Chip label="Expected value" value={fmtCurrency(ls.expected_value)} tone="good" />
        <Chip label="Submission channel" value={ls.submission_channel} />
      </ChipRow>
      {ch.length > 0 && (
        <div className="denials-list">
          <div className="denials-list__title">Chains fired</div>
          <ul className="denials-chain-list">
            {ch.map((c, i) => <li key={i}><code>{toText(c)}</code></li>)}
          </ul>
        </div>
      )}
      {sup.length > 0 && (
        <details className="denials-disclosure" open>
          <summary><strong>Supported issues</strong> &middot; {sup.length}</summary>
          <table className="denials-table">
            <thead><tr><th>Issue</th><th>Evidence citation</th><th>Chain</th></tr></thead>
            <tbody>{sup.map((s, i) => (
              <tr key={i}>
                <td className="denials-table__wrap">{toText(s.issue)}</td>
                <td className="denials-table__wrap">{toText(s.evidence_citation) || DASH}</td>
                <td><code>{toText(s.supported_by_chain_id) || DASH}</code></td>
              </tr>
            ))}</tbody>
          </table>
        </details>
      )}
      {unsup.length > 0 && (
        <details className="denials-disclosure">
          <summary><strong>Unsupported issues</strong> &middot; {unsup.length}</summary>
          <table className="denials-table">
            <thead><tr><th>Issue</th><th>Reason</th></tr></thead>
            <tbody>{unsup.map((s, i) => (
              <tr key={i}>
                <td className="denials-table__wrap">{toText(s.issue)}</td>
                <td className="denials-table__wrap">{toText(s.reason_unsupported) || DASH}</td>
              </tr>
            ))}</tbody>
          </table>
        </details>
      )}
      {ls.evidence_summary && <KVRow k="Evidence summary" v={ls.evidence_summary} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionHeader -- replaces the old "0", "A1", "C6" prefixes with a clean
// "A. Case Resolution Summary" style header that has the same colored
// background bar across all five RCM sections.
// ---------------------------------------------------------------------------
function RCMSection({ letter, title, hint, children }) {
  return (
    <section className={`denials-rcm-section denials-rcm-section--${(letter || '').toLowerCase()}`}>
      <header className="denials-rcm-section__head">
        <span className="denials-rcm-section__letter">{letter}</span>
        <span className="denials-rcm-section__title">{title}</span>
      </header>
      {hint && <p className="denials-rcm-section__hint">{hint}</p>}
      <div className="denials-rcm-section__body">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// A. Case Resolution Summary
//
// Single section that replaces the old A1 (Decision Summary), A2 (Financial
// Impact) and A8 (Recommended Action) blocks. One chip row of headline
// numbers + a 9-row key/value table so the RCM specialist can read the
// recovery picture in 10 seconds.
// ---------------------------------------------------------------------------
function deriveActionOwner(action) {
  const a = String(action || '').toLowerCase();
  if (!a) return DASH;
  if (a === 'file_appeal' || a === 'appeal') return 'RCM Specialist';
  if (a === 'submit_corrected_claim' || a === 'corrected_claim') return 'Coder';
  if (a === 'coder_review') return 'Coder';
  if (a === 'documentation_submission' || a === 'clinical_review') return 'Clinical Documentation';
  if (a === 'cob_resubmission' || a === 'auth_correction' || a === 'payer_follow_up') return 'Billing';
  if (a === 'underpayment_dispute') return 'Billing';
  if (a === 'write_off' || a === 'patient_responsibility') return 'Billing';
  return 'RCM Specialist';
}

// ---------------------------------------------------------------------------
// Mitigation availability detection.
//
// The backend gate engine + synthesizer now resolve EXACTLY ONE canonical
// recommendation per case and write it to `recommended_action.action` (one
// of: file_appeal, submit_corrected_claim, coder_review, write_off,
// human_review). That single field is authoritative and is the primary
// signal here -- a case "has mitigation" when its action is actionable
// (anything other than write_off / human_review).
//
// The correction_plan / payer_contradictions / appeal_letter checks below
// are retained only as a defensive secondary signal (e.g. partial / legacy
// payloads); for current payloads `recommended_action.action` alone decides.
// ---------------------------------------------------------------------------
const NON_MITIGATION_ACTIONS = new Set([
  'human_review', 'human_review_required', 'write_off',
  'patient_responsibility', '', 'none',
]);

function _actionIsMitigation(action) {
  if (!action) return false;
  return !NON_MITIGATION_ACTIONS.has(String(action).toLowerCase());
}

function deriveMitigationSignals(caseFile) {
  const ra = caseFile?.recommended_action || {};
  const plan = Array.isArray(caseFile?.correction_plan) ? caseFile.correction_plan : [];
  const contradictions = Array.isArray(caseFile?.payer_contradictions)
    ? caseFile.payer_contradictions : [];
  const packet = caseFile?.appeal_packet || {};

  const planActions = plan
    .map((r) => String(r?.action || '').toLowerCase())
    .filter((a) => _actionIsMitigation(a));
  const strongContradictions = contradictions.filter((c) => {
    const strength = String(c?.contradiction_strength || '').toLowerCase();
    const usability = String(c?.appeal_usability || '').toLowerCase();
    return (strength === 'strong' || strength === 'moderate')
      || (usability === 'high' || usability === 'medium');
  });
  const hasLetter = typeof packet.appeal_letter_text === 'string'
    && packet.appeal_letter_text.trim().length > 50;

  const hasMitigation = _actionIsMitigation(ra.action)
    || planActions.length > 0
    || strongContradictions.length > 0
    || hasLetter;

  // First actionable verb we can quote in the chip / table. Prefer the
  // top-level when it's already actionable, otherwise the first per-line
  // correction_plan action.
  const primaryMitigationAction = _actionIsMitigation(ra.action)
    ? String(ra.action).toLowerCase()
    : (planActions[0] || null);

  return { hasMitigation, primaryMitigationAction, planActions, strongContradictions, hasLetter };
}

function ownerForCase(caseFile, signals) {
  const top = caseFile?.recommended_action?.action;
  if (_actionIsMitigation(top)) return deriveActionOwner(top);
  if (signals && signals.primaryMitigationAction) {
    return deriveActionOwner(signals.primaryMitigationAction);
  }
  // No mitigation surfaced -- a human must triage.
  return 'RCM Specialist';
}

function deriveTotalCharged(caseFile) {
  const denialPayload = caseFile?.denial_payload || {};
  const denials = Array.isArray(denialPayload.denials) ? denialPayload.denials : [];
  let total = 0;
  let any = false;
  for (const d of denials) {
    if (!d || typeof d !== 'object') continue;
    const billed = Number(d.billed_amount ?? d.charge_amount ?? d.total_amount);
    if (Number.isFinite(billed) && billed > 0) { total += billed; any = true; }
  }
  if (any) return total;
  const fe = caseFile?.financial_estimate || {};
  const gross = Number(fe.gross_disputed);
  if (Number.isFinite(gross) && gross > 0) return gross;
  return null;
}

function deriveDenialPattern(decisionSummary) {
  const arch = Array.isArray(decisionSummary?.archetypes) ? decisionSummary.archetypes : [];
  if (arch.length === 0) {
    if (decisionSummary?.denial_family) return toText(decisionSummary.denial_family);
    return DASH;
  }
  return arch.map((a) => toText(a).replace(/_/g, ' ')).join(' + ');
}

function _mitigationRouteLabel(action) {
  if (!action) return null;
  const a = String(action).toLowerCase();
  if (a === 'file_appeal' || a === 'appeal') return 'Appeal-ready';
  if (a === 'submit_corrected_claim' || a === 'corrected_claim') return 'Corrected claim';
  if (a === 'documentation_submission') return 'Documentation request';
  if (a === 'payer_follow_up') return 'Payer follow-up';
  if (a === 'cob_resubmission') return 'COB resubmission';
  if (a === 'auth_correction') return 'Authorization correction';
  if (a === 'underpayment_dispute') return 'Underpayment dispute';
  return null;
}

function CaseResolutionSummary({ caseFile }) {
  if (!caseFile) return <Empty />;
  const ds = caseFile.decision_summary || {};
  const fe = caseFile.financial_estimate || {};
  const ra = caseFile.recommended_action || {};
  const totalCharged = deriveTotalCharged(caseFile);
  const signals = deriveMitigationSignals(caseFile);
  const mitigationFound = signals.hasMitigation ? 'Yes' : 'No';
  const denialType = [
    ds.denial_family ? toText(ds.denial_family) : null,
    ds.denial_subtype ? toText(ds.denial_subtype) : null,
  ].filter(Boolean).join(' \u2014 ') || DASH;
  // "Next Step" -- keep the top-level recommendation prominent (e.g. "Send
  // to human review") but, when the engine also surfaced an actionable
  // mitigation, mention it inline so the chip honestly reflects what's
  // available, not just the top-level label.
  const topNextStep = ra.action ? humanAction(ra.action) : DASH;
  const mitigationVerb = signals.primaryMitigationAction
    ? humanAction(signals.primaryMitigationAction) : null;
  const nextStep = (mitigationVerb && signals.primaryMitigationAction !== String(ra.action || '').toLowerCase())
    ? `${topNextStep} \u00b7 mitigation: ${mitigationVerb}`
    : topNextStep;
  const ownerLabel = ownerForCase(caseFile, signals);
  const routeLabel = _mitigationRouteLabel(signals.primaryMitigationAction)
    || _mitigationRouteLabel(ra.action) || '\u2014';
  const priority = toText(ds.priority) || 'medium';
  const confidence = ds.final_confidence_pct != null
    ? `${ds.final_confidence_pct}%`
    : fmtPct(ds.confidence);

  // Potential recovery as % of denied (used in the Financial Snapshot
  // sidebar so the specialist sees "55% of the denied $96.5k is in play").
  let potentialPct = null;
  const denied = Number(fe.denied_amount);
  const recover = Number(fe.expected_recovery);
  if (Number.isFinite(denied) && denied > 0 && Number.isFinite(recover) && recover >= 0) {
    potentialPct = Math.round((recover / denied) * 100);
  }

  const rows = [
    ['Total Amount Charged', totalCharged != null ? fmtCurrency(totalCharged) : DASH],
    ['Amount Denied',        fmtCurrency(fe.denied_amount)],
    ['Amount Recoverable',   fmtCurrency(fe.expected_recovery)],
    ['Denial Type',          denialType],
    ['Denial Pattern',       deriveDenialPattern(ds)],
    ['Denial Mitigation Found', mitigationFound],
    ['Recommended Next Step', nextStep],
    ['Confidence',           confidence],
    ['Priority',             priority],
    ['Action Owner',         ownerLabel],
  ];

  return (
    <div className="denials-rcm-summary">
      {/* Headline chip row -- the seven numbers a specialist scans first. */}
      <ChipRow>
        <Chip label="Amount Recoverable" value={fmtCurrency(fe.expected_recovery)} tone="good" />
        <Chip label="Confidence" value={confidence} tone={confTone(ds.final_confidence_pct ?? ds.confidence)} />
        <Chip label="Next Step" value={topNextStep} tone={signals.hasMitigation ? 'warn' : 'bad'} />
        <Chip label="Mitigation Found" value={mitigationFound} tone={mitigationFound === 'Yes' ? 'good' : 'bad'} />
        <Chip label="Route" value={routeLabel} tone={signals.hasMitigation ? 'good' : null} />
        <Chip label="Priority" value={priority} tone={priorityTone(priority)} />
        <Chip label="Action Owner" value={ownerLabel} />
      </ChipRow>

      {/* Two-column body: detailed KV table on the left, "Why" + Financial
          Snapshot sidebar on the right. */}
      <div className="denials-rcm-summary__grid">
        <table className="denials-table denials-rcm-summary-table">
          <tbody>
            {rows.map(([k, v], i) => (
              <tr key={i}>
                <th scope="row" className="denials-rcm-summary-table__k">{k}</th>
                <td className="denials-rcm-summary-table__v">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <aside className="denials-rcm-summary__aside">
          <div className="denials-rcm-card denials-rcm-card--why">
            <div className="denials-rcm-card__title">Why this recommendation?</div>
            <div className="denials-rcm-card__body">
              {ra.rationale
                ? toText(ra.rationale)
                : (ds.classification_rationale
                  ? toText(ds.classification_rationale)
                  : 'No rationale captured by the engine for this case.')}
            </div>
          </div>

          <div className="denials-rcm-card denials-rcm-card--finance">
            <div className="denials-rcm-card__title">Financial Snapshot</div>
            <dl className="denials-rcm-finance">
              <div className="denials-rcm-finance__row">
                <dt>Total Charged</dt>
                <dd>{totalCharged != null ? fmtCurrency(totalCharged) : DASH}</dd>
              </div>
              <div className="denials-rcm-finance__row">
                <dt>Denied</dt>
                <dd>{fmtCurrency(fe.denied_amount)}</dd>
              </div>
              <div className="denials-rcm-finance__row denials-rcm-finance__row--good">
                <dt>Recoverable</dt>
                <dd>{fmtCurrency(fe.expected_recovery)}</dd>
              </div>
              {potentialPct != null && (
                <div className="denials-rcm-finance__row">
                  <dt>Potential Recovery</dt>
                  <dd>{potentialPct}%</dd>
                </div>
              )}
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// B. Denial Findings & Mitigation Path
//
// One wide table per claim line. Folds A3 (denial issues), A4 (evidence
// timeline) and A5 (payer contradictions) into a single specialist-facing
// view with a new "Specialist Interpretation" column derived from the
// payer-contradictions block. Removes internal jargon ("contradiction",
// "source graph", "deterministic", "merge gate") from every visible label.
// ---------------------------------------------------------------------------
const ROUTE_LABEL = {
  file_appeal: 'Appeal-ready',
  appeal: 'Appeal-ready',
  medical_necessity_appeal: 'Appeal-ready',
  coder_review: 'Coder review',
  submit_corrected_claim: 'Corrected claim',
  corrected_claim: 'Corrected claim',
  documentation_submission: 'Documentation request',
  payer_follow_up: 'Payer follow-up',
  cob_resubmission: 'COB resubmission',
  auth_correction: 'Authorization correction',
  coding_review: 'Coding review',
  clinical_review: 'Clinical review',
  underpayment_dispute: 'Underpayment dispute',
  write_off: 'Write off',
  patient_responsibility: 'Patient responsibility',
  human_review: 'Manual review',
  human_review_required: 'Manual review',
};

function _bestContradictionForLine(contradictions, lineNo) {
  if (!Array.isArray(contradictions) || contradictions.length === 0) return null;
  const byLine = contradictions.find((c) => c && c.claim_line_no != null
    && Number(c.claim_line_no) === Number(lineNo));
  if (byLine) return byLine;
  return contradictions[0] || null;
}

function _evidenceForLine(checklist, lineNo) {
  if (!Array.isArray(checklist) || checklist.length === 0) return [];
  const scoped = checklist.filter((e) => e && (e.line_no == null || Number(e.line_no) === Number(lineNo)));
  return (scoped.length ? scoped : checklist).slice(0, 4);
}

function _humanizeInterpretation(c) {
  if (!c || typeof c !== 'object') return '';
  if (c.appeal_usability) return toText(c.appeal_usability);
  const evidence = toText(c.contradicting_evidence || c.cm_evidence);
  if (evidence) return `Payer reason conflicts with available documentation: ${evidence}`;
  return '';
}

function FindingsAndMitigation({ caseFile }) {
  if (!caseFile) return <Empty />;
  const denialPayload = caseFile.denial_payload || {};
  const denials = Array.isArray(denialPayload.denials) ? denialPayload.denials : [];
  const correction = Array.isArray(caseFile.correction_plan) ? caseFile.correction_plan : [];
  const contradictions = Array.isArray(caseFile.payer_contradictions) ? caseFile.payer_contradictions : [];
  const checklist = Array.isArray(caseFile.evidence_checklist) ? caseFile.evidence_checklist : [];
  const findingsUi = Array.isArray(caseFile.denial_findings_mitigation_path_ui)
    ? caseFile.denial_findings_mitigation_path_ui
    : [];
  const primaryAction = caseFile.primary_action || caseFile.recommended_action;

  // Prefer the synthesizer's purpose-built findings block; fall back to the
  // correction plan, then to the raw denial payload.
  let baseRows;
  if (findingsUi.length > 0) {
    baseRows = findingsUi.map((d) => {
      const [carc, rarc] = String(d.denial_code_issue || '').split('/');
      return {
        line_no: d.line,
        carc: (carc || '').trim(),
        rarc: (rarc || '').trim(),
        issue: '',
        payer_reason: d.payer_reason,
        action: d.mitigation_action || primaryAction || d.route,
        route_label: d.route,
        channel: '-',
        rationale: d.specialist_interpretation,
        supporting_evidence: Array.isArray(d.supporting_evidence) ? d.supporting_evidence : [],
      };
    });
  } else if (correction.length > 0) {
    baseRows = correction.map((r) => ({
      line_no: r.line_no,
      carc: r.carc, rarc: r.rarc,
      issue: r.issue, payer_reason: r.payer_reason || r.rationale,
      action: r.action, channel: r.channel,
      rationale: r.rationale,
    }));
  } else {
    baseRows = denials.map((d) => ({
      line_no: d.line_no,
      carc: d.carc, rarc: d.rarc,
      issue: d.payer_note,
      payer_reason: d.payer_note,
      action: 'human_review',
      channel: '-',
      rationale: '',
    }));
  }

  if (baseRows.length === 0) return <Empty />;

  const denialByLine = new Map();
  denials.forEach((d) => { if (d && d.line_no != null) denialByLine.set(d.line_no, d); });

  return (
    <table className="denials-table denials-table--wide denials-rcm-findings-table">
      <thead>
        <tr>
          <th>Line</th>
          <th>Denial Code / Issue</th>
          <th>Payer Reason</th>
          <th>Supporting Evidence</th>
          <th>Specialist Interpretation</th>
          <th>Mitigation Action</th>
          <th>Route</th>
        </tr>
      </thead>
      <tbody>
        {baseRows.map((r, i) => {
          const d = denialByLine.get(r.line_no) || {};
          const carc = toText(r.carc || d.carc) || DASH;
          const rarc = toText(r.rarc || d.rarc) || DASH;
          const issueText = toText(r.issue || d.payer_note) || DASH;
          const payerReason = toText(r.payer_reason || d.payer_note) || DASH;
          const rowEvidence = Array.isArray(r.supporting_evidence) && r.supporting_evidence.length > 0
            ? r.supporting_evidence.map((e) => toText(typeof e === 'string' ? e : (e.required_evidence || e.notes || e.label))).filter(Boolean)
            : _evidenceForLine(checklist, r.line_no).map((e) => toText(e.required_evidence || e.notes)).filter(Boolean);
          const evidenceTxt = rowEvidence.length === 0 ? DASH : rowEvidence.join('; ');
          const interpretation = toText(r.rationale)
            || _humanizeInterpretation(_bestContradictionForLine(contradictions, r.line_no))
            || DASH;
          const routeKey = String(r.route_label || r.action || '').toLowerCase();
          const route = ROUTE_LABEL[routeKey] || (r.route_label || r.action ? humanAction(r.route_label || r.action) : DASH);
          return (
            <tr key={i}>
              <td>{r.line_no != null ? `#${r.line_no}` : DASH}</td>
              <td>
                <code>{carc}</code> / <code>{rarc}</code>
                {issueText !== DASH && (
                  <div className="denials-table__sub">{issueText}</div>
                )}
              </td>
              <td className="denials-table__wrap">{payerReason}</td>
              <td className="denials-table__wrap">{evidenceTxt}</td>
              <td className="denials-table__wrap">{interpretation}</td>
              <td>
                <span className={`denials-pill denials-pill--${actionTone(r.action) || 'warn'}`}>
                  {humanAction(r.action)}
                </span>
              </td>
              <td>{route}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// C. Clinical & Claim Context
//
// Replaces the freeform "Universal Case Context" panel with a numbered
// table. The case-context builder produces predictable English paragraphs
// (Payer ... / Service line(s) ... / Patient ... / Payer denied ...),
// so we anchor-parse those into rows and fall back to a numbered paragraph
// list if any anchor is absent so we never lose data.
// ---------------------------------------------------------------------------
function _splitContextParagraphs(text) {
  if (!text || typeof text !== 'string') return [];
  return text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

function _parseClaimContextRows(text) {
  const paragraphs = _splitContextParagraphs(text);
  const rows = [];
  let n = 1;
  const push = (item, detail, why) => {
    if (!detail) return;
    rows.push({ n: n++, item, detail, why });
  };
  for (const p of paragraphs) {
    const lower = p.toLowerCase();
    if (lower.includes('claim') && (lower.includes('submitted to') || lower.includes('payer'))) {
      push('Payer & Claim', p, 'Determines appeal vs corrected claim route');
    } else if (lower.includes('service line') || lower.includes('cpt')) {
      push('Service Lines Billed', p, 'Anchors coding / modifier analysis');
    } else if (lower.includes('patient') || lower.includes('member id')) {
      push('Patient', p, 'Eligibility and demographic context');
    } else if (lower.includes('denied') && lower.includes('$')) {
      push('Denial & Financials', p, 'Prioritizes the work queue');
    } else if (lower.includes('clinical') || lower.includes('admission') || lower.includes('diagnosis')) {
      push('Clinical Context', p, 'Supports medical necessity');
    } else if (lower.includes('history') || lower.includes('appeal')) {
      push('History', p, 'Prior outcomes inform strategy');
    } else {
      push('Additional Context', p, 'Background');
    }
  }
  return rows;
}

function ClinicalAndClaimContext({ text }) {
  if (!text || typeof text !== 'string') return <Empty />;
  const rows = _parseClaimContextRows(text);
  if (rows.length === 0) return <Empty />;
  return (
    <table className="denials-table denials-table--wide denials-rcm-context-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Context Item</th>
          <th>Case Detail</th>
          <th>Why It Matters</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.n}>
            <td>{r.n}</td>
            <td>{r.item}</td>
            <td className="denials-table__wrap">{r.detail}</td>
            <td className="denials-table__wrap">{r.why}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// D. Evidence Discovery Approach (renamed from "Evidence Checklist")
//
// Same `evidence_checklist` data but presented as four columns: Evidence
// Area / What Was Found / Why It Matters / RCM Use. Preserves all clinical
// detail; just translates engine status into next-step RCM actions.
// ---------------------------------------------------------------------------
function _rcmUseForStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'found') return 'Attach to packet';
  if (s === 'partial' || s === 'conditional') return 'Cite + augment in appeal';
  if (s === 'missing' || s === 'not_found') return 'Request from provider';
  if (s === 'not_applicable' || s === 'na') return '\u2014';
  return 'Review';
}

function _whyItMattersDefault(area) {
  const a = String(area || '').toLowerCase();
  if (a.includes('clinical') || a.includes('note')) return 'Supports medical necessity';
  if (a.includes('lab') || a.includes('imag')) return 'Objective diagnostic support';
  if (a.includes('procedure') || a.includes('op')) return 'Links billed service to care event';
  if (a.includes('auth')) return 'Required when payer asserts no authorization';
  if (a.includes('policy')) return 'Anchors payer-policy citations in the appeal';
  if (a.includes('coding') || a.includes('modifier')) return 'Validates coding before resubmission';
  if (a.includes('order')) return 'Documents the physician decision-making';
  return 'Strengthens the appeal packet';
}

function EvidenceDiscoveryApproach({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty />;
  return (
    <table className="denials-table denials-table--wide denials-rcm-evidence-table">
      <thead>
        <tr>
          <th>Evidence Area</th>
          <th>What Was Found</th>
          <th>Why It Matters</th>
          <th>RCM Use</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e, i) => {
          const found = String(e.found_status || '').toLowerCase();
          const tone = found === 'found' ? 'good'
            : (found === 'partial' || found === 'conditional') ? 'warn' : 'bad';
          const detail = toText(e.notes)
            || `${toText(e.found_status) || 'unknown'}${e.strength ? ' (' + toText(e.strength) + ' strength)' : ''}`;
          return (
            <tr key={i}>
              <td>{toText(e.required_evidence) || DASH}</td>
              <td className="denials-table__wrap">
                <span className={`denials-pill denials-pill--${tone}`}>{toText(e.found_status) || 'unknown'}</span>
                <div className="denials-table__sub">{detail}</div>
              </td>
              <td className="denials-table__wrap">{toText(e.why_it_matters) || _whyItMattersDefault(e.required_evidence)}</td>
              <td>{_rcmUseForStatus(e.found_status)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// E. Appeal / Submission Packet
//
// Streamlined replacement for the old C1-C5 stack. Four subsections:
//   E1 Appeal Letter Draft   -- collapsed by default, with a Copy button
//   E2 Required Attachments  -- merged attachment_checklist + evidence_table
//   E3 Policy / Coding Crosswalk
//   E4 Submission Instructions (recommended_action.steps relocated here)
// Removes the duplicate key-arguments / corrected-claim sections that used
// to live in C-Packet.
// ---------------------------------------------------------------------------
function _copyToClipboard(text) {
  if (!text) return;
  try {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
  } catch (_e) {
    // Best-effort copy. Some browsers block writeText outside a user
    // gesture; the <details>/<button> click is a gesture so this normally
    // succeeds, but we silently swallow errors so the UI never crashes.
  }
}

function AppealSubmissionPacket({ caseFile }) {
  const packet = (caseFile && caseFile.appeal_packet) || {};
  const ra = (caseFile && caseFile.recommended_action) || {};
  const appealRecord = caseFile && caseFile.appeal_record;
  const evRows = Array.isArray(packet.evidence_table) ? packet.evidence_table : [];
  const acRows = Array.isArray(packet.attachment_checklist) ? packet.attachment_checklist : [];
  const pcRows = Array.isArray(packet.policy_crosswalk) ? packet.policy_crosswalk : [];
  const steps  = Array.isArray(ra.steps) ? ra.steps : [];
  const letter = toText(packet.appeal_letter_text);

  const anyContent = letter || acRows.length > 0 || pcRows.length > 0 || steps.length > 0
    || packet.corrected_claim_instructions || packet.documentation_submission_instructions;
  if (!anyContent) return <Empty />;

  // ---- Build the merged Required Attachments rows from
  // attachment_checklist (operational list) + evidence_table (clinical
  // backing). De-dup on name where possible.
  const attachmentRows = [];
  const seen = new Set();
  acRows.forEach((a) => {
    const name = (typeof a === 'object' && a !== null) ? (a.name || a.label) : a;
    const key = String(name || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    attachmentRows.push({
      name: toText(name) || '(unnamed attachment)',
      required: 'Required',
      status: 'Pending',
      notes: typeof a === 'object' ? toText(a.purpose || a.notes) : '',
    });
  });
  evRows.forEach((e) => {
    const name = toText(e.evidence) || toText(e.required_evidence);
    const key = String(name || '').toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    attachmentRows.push({
      name,
      required: 'Recommended',
      status: 'Found',
      notes: [toText(e.date), toText(e.supports_chain_id)].filter(Boolean).join(' \u00b7 '),
    });
  });

  return (
    <div className="denials-rcm-packet">
      {appealRecord && (
        <ChipRow>
          <Chip label="Appeal ID" value={appealRecord.appeal_id?.slice(0, 8)} title={appealRecord.appeal_id} />
          <Chip label="Level" value={appealRecord.appeal_level} />
          <Chip label="Status" value={appealRecord.status}
                tone={appealRecord.status === 'won' ? 'good'
                  : appealRecord.status === 'lost' ? 'bad' : 'warn'} />
          <Chip label="Policy clauses" value={appealRecord.policy_crosswalk_count} />
          <Chip label="Expected recovery" value={fmtCurrency(appealRecord.expected_recovery)} tone="good" />
          <Chip label="Confidence" value={fmtPct(appealRecord.confidence)} tone={confTone(appealRecord.confidence)} />
        </ChipRow>
      )}

      {letter && (
        <details className="denials-disclosure">
          <summary>
            <strong>E1. Appeal Letter Draft</strong>
            <button
              type="button"
              className="denials-btn denials-btn--ghost denials-rcm-packet__copy"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); _copyToClipboard(letter); }}
            >
              Copy
            </button>
          </summary>
          <pre className="denials-letter">{letter}</pre>
        </details>
      )}

      {attachmentRows.length > 0 && (
        <details className="denials-disclosure" open>
          <summary><strong>E2. Required Attachments</strong> &middot; {attachmentRows.length}</summary>
          <table className="denials-table">
            <thead><tr><th>Attachment</th><th>Required?</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {attachmentRows.map((a, i) => (
                <tr key={i}>
                  <td>{a.name}</td>
                  <td>{a.required}</td>
                  <td>
                    <span className={`denials-pill denials-pill--${a.status === 'Found' ? 'good' : 'warn'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="denials-table__wrap">{a.notes || DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {pcRows.length > 0 && (
        <details className="denials-disclosure" open>
          <summary><strong>E3. Policy / Coding Crosswalk</strong> &middot; {pcRows.length} clause{pcRows.length === 1 ? '' : 's'}</summary>
          <table className="denials-table denials-table--wide">
            <thead><tr><th>Topic</th><th>Relevant Finding</th><th>How to Use</th></tr></thead>
            <tbody>
              {pcRows.map((p, i) => (
                <tr key={i}>
                  <td><code>{toText(p.policy_graph_ref) || toText(p.topic) || DASH}</code></td>
                  <td className="denials-table__wrap"><blockquote>{toText(p.clause)}</blockquote></td>
                  <td className="denials-table__wrap">{toText(p.appeal_argument_paragraph) || toText(p.how_to_use) || DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {(steps.length > 0 || packet.corrected_claim_instructions || packet.documentation_submission_instructions) && (
        <details className="denials-disclosure" open>
          <summary><strong>E4. Submission Instructions</strong></summary>
          <div className="denials-rcm-packet__steps">
            {steps.length > 0 && (
              <ol className="denials-list__ol">
                {steps.map((s, i) => <li key={i}>{toText(s)}</li>)}
              </ol>
            )}
            {packet.corrected_claim_instructions && (
              <div className="denials-rcm-packet__inline">
                <strong>Corrected claim: </strong>{toText(packet.corrected_claim_instructions)}
              </div>
            )}
            {packet.documentation_submission_instructions && (
              <div className="denials-rcm-packet__inline">
                <strong>Documentation submission: </strong>{toText(packet.documentation_submission_instructions)}
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level
// ---------------------------------------------------------------------------

function DenialResolveV4View({ caseFile, loading, error, onRefresh }) {
  if (loading) {
    return (
      <div className="denials-processing-card">
        <div className="denials-processing-card__spinner" aria-hidden="true" />
        <div className="denials-processing-card__title">Loading v4 Case File&hellip;</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="denials-processing-card denials-processing-card--error">
        <div className="denials-processing-card__title">v4 Case File unavailable</div>
        <p className="denials-processing-card__text">{error}</p>
        {onRefresh && (
          <button type="button" className="denials-btn" onClick={onRefresh}>
            Try again
          </button>
        )}
      </div>
    );
  }
  if (!caseFile || typeof caseFile !== 'object' ||
      Object.keys(caseFile).length === 0) {
    return (
      <div className="denials-processing-card">
        <div className="denials-processing-card__title">
          No v4 Case File persisted for this case yet
        </div>
        <p className="denials-processing-card__text">
          Run the case at <code>engine=v4</code> to generate the
          13-section Denial Resolution Case File.
        </p>
      </div>
    );
  }
  return (
    <div className="denials-v4-result denials-v4-result--rcm">
      <RCMSection
        letter="A"
        title="Case Resolution Summary"
        hint="What's at stake and the recommended next step.">
        <CaseResolutionSummary caseFile={caseFile} />
      </RCMSection>

      <RCMSection
        letter="B"
        title="Denial Findings & Mitigation Path"
        hint="Per-line denial codes, payer reasoning, supporting evidence, and the recommended mitigation route.">
        <FindingsAndMitigation caseFile={caseFile} />
      </RCMSection>

      <RCMSection
        letter="C"
        title="Clinical & Claim Context"
        hint="The case background threaded into every prompt the engine ran.">
        <ClinicalAndClaimContext text={caseFile.case_context} />
      </RCMSection>

      <RCMSection
        letter="D"
        title="Evidence Discovery Approach"
        hint="How the engine searched for clinical and coding support, and what each finding means for the appeal.">
        <EvidenceDiscoveryApproach rows={caseFile.evidence_checklist} />
      </RCMSection>

      <RCMSection
        letter="E"
        title="Appeal / Submission Packet"
        hint="Appeal letter draft, required attachments, policy crosswalk, and submission instructions.">
        <AppealSubmissionPacket caseFile={caseFile} />
      </RCMSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Named exports of the legacy section components so the DeveloperDrawer in
// `DenialCaseRunPage.js` can keep rendering them under a collapsed "Engine
// internals" panel without re-importing the underlying data shape.
// ---------------------------------------------------------------------------
export {
  S2_RootCause,
  S3_LookupTrace,
  S4_SourceGraphTrace,
  S6_DeterministicFindings,
  S7_SNNFindings,
  S8ext_PayerContradictions,
  S11_PacketOutputs,
  S12_HumanReviewFlags,
  S13_LearningTags,
  S13ext_LearningSignal,
  C6_AuditTrail,
};

export default DenialResolveV4View;
